import { DefaultValues, FieldPath, FormProvider, type FieldErrors, type Resolver, useForm } from 'react-hook-form';
import { ArrowLeftStroke, FloppyDisk1Stroke } from '@lineiconshq/free-icons';
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { extractApiErrorMessage } from '@/lib/error-utils';
import { ActionButtonV2 } from '../action-button-v2';
import { ActionBarV2 } from '../action-bar-v2';
import { FormMode } from '@/types/form';
import { cn } from '@/lib/utils';
import { toast } from '@/hooks';

import { AritoFormV2Context } from './context';

export type TabItemV2 = {
  id: string;
  label: string;
  component: ReactNode;
};

export interface AritoFormV2Props<TFormData extends Record<string, any>> {
  mode?: FormMode;
  hasAritoActionBar?: boolean;
  title?: string;
  subTitle?: string;
  initialData?: Partial<TFormData>;
  onSubmit?: (data: TFormData) => void | Promise<void>;
  onValuesChange?: (data: Partial<TFormData>) => void;
  schema?: z.ZodType<any>;
  headerFields?: ReactNode | TabItemV2[];
  headerTabActions?: ReactNode;
  tabs?: ReactNode | TabItemV2[];
  onClose?: () => void;
  from?: ReactNode;
  headerBar?: ReactNode;
  bottomBar?: ReactNode;
  actionButtons?: ReactNode;
  className?: string;
  classNameHeaderBar?: string;
  loading?: boolean;
  getInvalidSubmitMessage?: (errors: FieldErrors<TFormData>) => string | undefined;
}

const tabListClassName = 'h-8 w-full justify-start rounded-none border-0 p-0 text-slate-600';

const headerTabTriggerClassName =
  'relative inline-flex h-8 items-center justify-center rounded-none border-0 bg-transparent px-4 py-0 text-[12px] font-semibold leading-none text-slate-500 shadow-none ring-0 transition-colors after:absolute after:bottom-0 after:left-0 after:h-[3px] after:w-full after:bg-main after:opacity-0 hover:bg-transparent hover:text-main hover:after:opacity-100 focus-visible:ring-0 data-[state=active]:bg-main/10 data-[state=active]:text-main data-[state=active]:shadow-none data-[state=active]:after:opacity-100';

const bodyTabTriggerClassName =
  'relative inline-flex h-8 items-center justify-center rounded-none border-0 bg-transparent px-4 py-0 text-[12px] font-semibold leading-none text-slate-500 shadow-none ring-0 transition-colors after:absolute after:bottom-0 after:left-0 after:h-[3px] after:w-full after:bg-main after:opacity-0 hover:bg-transparent hover:text-main hover:after:opacity-100 focus-visible:ring-0 data-[state=active]:bg-main/10 data-[state=active]:text-main data-[state=active]:shadow-none data-[state=active]:after:opacity-100';

const invalidFieldSelector = '[aria-invalid="true"]';
const focusableSelector =
  'input:not([disabled]), textarea:not([disabled]), select:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])';
const isVisible = (element: HTMLElement) => {
  if (!element.getClientRects().length) {
    return false;
  }

  const style = window.getComputedStyle(element);
  return style.visibility !== 'hidden' && style.display !== 'none';
};

const focusFirstError = (formElement: HTMLFormElement | null) => {
  if (!formElement || typeof window === 'undefined') {
    return;
  }

  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => {
      const invalidElement = Array.from(formElement.querySelectorAll<HTMLElement>(invalidFieldSelector)).find(
        isVisible
      );

      if (!invalidElement) {
        return;
      }

      const focusTarget = invalidElement.matches(focusableSelector)
        ? invalidElement
        : invalidElement.querySelector<HTMLElement>(focusableSelector);

      invalidElement.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      focusTarget?.focus({ preventScroll: true });
    });
  });
};

const errorPayload = (error: unknown): Record<string, unknown> | null => {
  if (!error || typeof error !== 'object') return null;

  const value = error as any;
  const payload = value?.response?.data ?? value?.data ?? value;

  return payload && typeof payload === 'object' && !Array.isArray(payload) ? payload : null;
};

const errorFields = (error: unknown): string[] => {
  const payload = errorPayload(error);
  const rawFields = payload?.error_fields;

  if (!Array.isArray(rawFields)) return [];

  return rawFields.filter((field): field is string => typeof field === 'string' && field.trim().length > 0);
};

function FormTabsSectionV2({
  items,
  value,
  onValueChange,
  triggerClassName,
  actions,
  contentClassName,
  contentWrapperClassName,
  tabsClassName,
  listClassName
}: {
  items: TabItemV2[];
  value: string;
  onValueChange: (value: string) => void;
  triggerClassName: string;
  actions?: ReactNode;
  contentClassName?: string;
  contentWrapperClassName?: string;
  tabsClassName?: string;
  listClassName?: string;
}) {
  return (
    <Tabs
      value={value}
      onValueChange={onValueChange}
      className={cn('flex h-full min-h-0 flex-1 flex-col overflow-hidden', tabsClassName)}
    >
      <div className='flex items-stretch justify-between border-b border-[#d7d7d7] bg-muted'>
        <TabsList className={cn(tabListClassName, 'bg-muted', listClassName)}>
          {items.map(item => (
            <TabsTrigger key={item.id} value={item.id} className={triggerClassName}>
              {item.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {actions ? <div className='flex items-center px-2'>{actions}</div> : null}
      </div>

      {items.map(item => (
        <TabsContent
          key={item.id}
          value={item.id}
          className={cn(
            'mt-0 min-h-0 w-full flex-1 outline-none data-[state=active]:flex data-[state=inactive]:hidden data-[state=active]:flex-col',
            contentClassName
          )}
        >
          <div className={cn('flex h-full min-h-0 w-full min-w-0 flex-col overflow-hidden', contentWrapperClassName)}>
            {item.component}
          </div>
        </TabsContent>
      ))}
    </Tabs>
  );
}

export const AritoFormV2 = <TFormData extends Record<string, any>>({
  mode = 'edit',
  title,
  hasAritoActionBar = true,
  subTitle,
  initialData,
  onSubmit,
  onValuesChange,
  schema,
  headerFields,
  headerTabActions,
  tabs,
  onClose,
  headerBar,
  bottomBar,
  actionButtons,
  className,
  classNameHeaderBar,
  loading = false,
  getInvalidSubmitMessage
}: AritoFormV2Props<TFormData>) => {
  const [activeTab, setActiveTab] = useState<string>(Array.isArray(tabs) ? tabs[0]?.id || '' : '');
  const [activeHeaderTab, setActiveHeaderTab] = useState<string>(
    Array.isArray(headerFields) ? headerFields[0]?.id || '' : ''
  );
  const formRef = useRef<HTMLFormElement | null>(null);
  const schemaRef = useRef(schema);
  schemaRef.current = schema;
  const resolver = useMemo<Resolver<TFormData>>(
    () => (values, context, options) => {
      const currentSchema = schemaRef.current;

      if (!currentSchema) {
        return {
          values,
          errors: {}
        };
      }

      return zodResolver(currentSchema)(values, context, options);
    },
    []
  );

  const methods = useForm<TFormData>({
    defaultValues: initialData as DefaultValues<TFormData>,
    mode: 'onSubmit',
    resolver
  });

  const {
    handleSubmit,
    formState: { errors, isSubmitting },
    control,
    reset,
    watch,
    getValues,
    setError
  } = methods;

  useEffect(() => {
    if (initialData) {
      const currentValues = getValues() as Partial<TFormData>;

      if (JSON.stringify(currentValues) !== JSON.stringify(initialData)) {
        reset(initialData as DefaultValues<TFormData>);
      }
    }
  }, [getValues, initialData, reset]);

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      console.log('Form validation errors:', errors);
    }
  }, [errors]);

  useEffect(() => {
    if (!onValuesChange) {
      return;
    }

    const subscription = watch(values => {
      onValuesChange(values as Partial<TFormData>);
    });

    return () => subscription.unsubscribe();
  }, [onValuesChange, watch]);

  const isViewMode = mode === 'view';
  const displayLoading = loading || isSubmitting;
  const headerHasTabs = Array.isArray(headerFields);
  const tabsHasTabs = Array.isArray(tabs);
  const contextValue = useMemo(
    () => ({ control, errors, isViewMode, loading: displayLoading }),
    [control, displayLoading, errors, isViewMode]
  );

  const handleClose = () => {
    onClose?.();
  };

  const onFormSubmit = async (data: TFormData) => {
    try {
      await onSubmit?.(data);
    } catch (error) {
      const message = extractApiErrorMessage({ data: error });
      const fields = errorFields(error);

      fields.forEach(field => {
        setError(field as FieldPath<TFormData>, {
          type: 'server',
          message
        });
      });

      toast({
        description: message,
        variant: 'destructive'
      });

      if (fields.length > 0) {
        focusFirstError(formRef.current);
      }
    }
  };

  const handleInvalidSubmit = (invalidErrors: FieldErrors<TFormData>) => {
    focusFirstError(formRef.current);
    toast({
      description:
        getInvalidSubmitMessage?.(invalidErrors) || 'Vui lòng kiểm tra lại các trường thông tin không hợp lệ.',
      variant: 'destructive'
    });
  };

  return (
    <FormProvider {...methods}>
      <AritoFormV2Context.Provider value={contextValue}>
        <div
          className={cn(
            'relative flex h-full min-h-0 w-full flex-col overflow-hidden bg-muted text-[length:var(--erp-form-text-size)]',
            className
          )}
          data-arito-form-v2
        >
          <div
            className='flex min-h-0 flex-1 flex-col bg-white'
            onKeyDown={event => {
              if (event.key === 'Enter') {
                event.preventDefault();
                event.stopPropagation();
              }
            }}
          >
            <form
              ref={formRef}
              onSubmit={event => {
                event.stopPropagation();
                void handleSubmit(onFormSubmit, handleInvalidSubmit)(event);
              }}
              className='flex min-h-0 flex-1 flex-col overflow-hidden'
            >
              {hasAritoActionBar && (
                <ActionBarV2
                  inForm={true}
                  loading={displayLoading}
                  className='bg-muted'
                  titleComponent={
                    <div className='flex flex-col items-center justify-center lg:items-start lg:justify-start'>
                      <h1 className='text-[length:var(--erp-action-bar-title-size)] font-bold'>
                        {title || (mode === 'add' && 'Thêm')}
                      </h1>
                      <h2 className='text-xs text-gray-500'>{subTitle}</h2>
                    </div>
                  }
                >
                  {actionButtons || (
                    <>
                      {!isViewMode && (
                        <ActionButtonV2
                          icon={FloppyDisk1Stroke}
                          title='Lưu'
                          variant='secondary'
                          type='submit'
                          disabled={displayLoading}
                        />
                      )}
                      <ActionButtonV2 icon={ArrowLeftStroke} title='Đóng' variant='destructive' onClick={handleClose} />
                    </>
                  )}
                </ActionBarV2>
              )}

              {headerBar && (
                <div className={cn('w-full border-b border-gray-300', classNameHeaderBar)}>{headerBar}</div>
              )}

              <div
                className='flex min-h-0 flex-1 flex-col overflow-y-auto overflow-x-hidden'
                data-arito-form-scroll-content
              >
                {headerFields && (
                  <div className='min-h-0 flex-none border-none'>
                    {headerHasTabs ? (
                      <FormTabsSectionV2
                        items={headerFields as TabItemV2[]}
                        value={activeHeaderTab}
                        onValueChange={setActiveHeaderTab}
                        triggerClassName={headerTabTriggerClassName}
                        actions={headerTabActions}
                        tabsClassName='h-auto flex-none overflow-visible'
                        contentClassName='flex-none overflow-visible'
                        contentWrapperClassName='h-auto overflow-visible'
                      />
                    ) : (
                      headerFields
                    )}
                  </div>
                )}

                {tabs &&
                  (tabsHasTabs ? (
                    <FormTabsSectionV2
                      items={tabs as TabItemV2[]}
                      value={activeTab}
                      onValueChange={setActiveTab}
                      triggerClassName={bodyTabTriggerClassName}
                      tabsClassName='h-auto flex-none overflow-visible'
                      contentClassName='flex-none overflow-visible'
                      contentWrapperClassName='h-auto overflow-visible'
                      listClassName='justify-start'
                    />
                  ) : (
                    <div className='flex min-h-0 flex-none flex-col overflow-visible'>{tabs}</div>
                  ))}
              </div>

              {bottomBar && (
                <div
                  className='relative z-10 max-h-[104px] w-full flex-none shrink-0 overflow-x-auto overflow-y-hidden border-t border-gray-200 bg-white'
                  data-arito-bottom-bar
                  data-arito-bottom-bar-flow='fixed'
                >
                  {bottomBar}
                </div>
              )}
            </form>
          </div>
        </div>
      </AritoFormV2Context.Provider>
    </FormProvider>
  );
};
